import { useCallback, useEffect, useMemo, useReducer } from 'react';
import type { Equipment, SessionTotals, SetSource, SetStatus, UnitSystem } from '@lift2fit/shared-types';
import { DEMO_PRIOR_BESTS, DEMO_SESSION, DEMO_UNIT, type DemoRowSeed, type DemoSession } from '@/data/demoSession';
import { detectPr, foldBests, type BestMap } from './prEngine';
import type { SetMeasure } from './prescription';
import { uid } from './format';
import { readUnitPreference, writeUnitPreference } from './useUnitPreference';

const STORAGE_KEY = 'l2f.grid.v2';
const MAX_UNDO = 25;

export interface GridSet {
  id: string;
  weightKg: number;
  reps: number;
  rpe: number | null;
  status: SetStatus;
  isPr: boolean;
  isWarmup: boolean;
  source: SetSource;
  committedAt: string | null;
  /** How the set is quantified — the trainer's sheet mixes reps, distance and time. */
  measure: SetMeasure;
  distanceM: number | null;
  durationS: number | null;
  perSide: boolean;
  /** VAS 0-10, straight off the programme's pain scale. */
  painScore: number | null;
}

export interface GridRow {
  id: string;
  exerciseId: string;
  /** Movement and variant, as the programme names them: SQUAT / SSB. */
  name: string;
  variant: string | null;
  muscleGroup: string;
  equipment: Equipment;
  modifications: string | null;
  isCompound: boolean;
  measure: SetMeasure;
  perSide: boolean;
  isBodyweight: boolean;
  /** Prescribed lifting tempo, e.g. 3-1-1-0. */
  tempo: string | null;
  /** Rows sharing a group number are performed back to back. */
  supersetGroup: number | null;
  prescribed: {
    sets: number;
    repMin: number;
    repMax: number;
    targetRpe: number | null;
    targetWeightKg: number | null;
    targetDistanceM: number | null;
    restSeconds: number;
  };
  coachCue: string | null;
  notes: string;
  sets: GridSet[];
}

export interface PostSessionScore {
  mood: number | null;
  rpe: number | null;
  pain: number | null;
}

export interface PrEvent {
  exerciseName: string;
  weightKg: number;
  reps: number;
  e1rm: number;
  previousBest: number | null;
  deltaKg: number;
}

interface State {
  rows: GridRow[];
  unit: UnitSystem;
  priorBests: BestMap;
  past: GridRow[][];
  lastPr: PrEvent | null;
  hydrated: boolean;
  postSession: PostSessionScore;
}

type FieldPatch = Partial<Pick<GridSet, 'weightKg' | 'reps' | 'rpe' | 'distanceM' | 'durationS' | 'painScore'>>;

type Action =
  | { type: 'hydrate'; rows: GridRow[]; unit: UnitSystem }
  | { type: 'field'; rowId: string; setId: string; patch: FieldPatch }
  | { type: 'commit'; rowId: string; setId: string; patch?: FieldPatch; source?: SetSource }
  | { type: 'status'; rowId: string; setId: string; status: SetStatus }
  | { type: 'add'; rowId: string; count?: number }
  | { type: 'delete'; rowId: string; setId: string }
  | { type: 'note'; rowId: string; note: string }
  | { type: 'unit'; unit: UnitSystem }
  | { type: 'post'; patch: Partial<PostSessionScore> }
  | { type: 'undo' }
  | { type: 'reset' }
  | { type: 'clearPr' };

function seedSet(seed: DemoRowSeed['sets'][number], row: DemoRowSeed): GridSet {
  return {
    id: uid('set'),
    weightKg: seed.weightKg,
    reps: seed.reps,
    rpe: seed.rpe,
    status: seed.status,
    isPr: seed.status === 'pr',
    isWarmup: seed.isWarmup ?? false,
    source: 'web',
    committedAt: seed.status === 'pending' ? null : DEMO_SESSION.startedAt,
    measure: seed.measure ?? row.measure,
    distanceM: seed.distanceM ?? null,
    durationS: seed.durationS ?? null,
    perSide: row.perSide,
    painScore: seed.painScore ?? null,
  };
}

function buildRows(session: DemoSession): GridRow[] {
  return session.rows.map((row) => ({
    id: row.id,
    exerciseId: row.exerciseId,
    name: row.variant ? `${row.movement}, ${row.variant}` : row.movement,
    variant: row.variant,
    muscleGroup: row.muscleGroup,
    equipment: row.equipment,
    modifications: row.modifications,
    isCompound: row.isCompound,
    measure: row.measure,
    perSide: row.perSide,
    isBodyweight: row.isBodyweight,
    tempo: row.tempo,
    supersetGroup: row.supersetGroup,
    prescribed: { ...row.prescribed, targetDistanceM: row.prescribed.targetDistanceM ?? null },
    coachCue: row.coachCue,
    notes: row.notes,
    sets: row.sets.map((set) => seedSet(set, row)),
  }));
}

function initialState(session: DemoSession): State {
  return {
    rows: buildRows(session),
    unit: readUnitPreference(),
    priorBests: DEMO_PRIOR_BESTS,
    past: [],
    lastPr: null,
    hydrated: false,
    postSession: { ...session.postSession },
  };
}

function withHistory(state: State, rows: GridRow[]): State {
  return { ...state, rows, past: [...state.past, state.rows].slice(-MAX_UNDO) };
}

/** Every committed set in the session, folded onto the historical bests, minus one set. */
function bestsExcluding(rows: GridRow[], exceptSetId: string | null): BestMap {
  const candidates = rows.flatMap((row) =>
    row.sets
      .filter((set) => set.id !== exceptSetId)
      .map((set) => ({
        exerciseId: row.exerciseId,
        weightKg: set.weightKg,
        reps: set.reps,
        status: set.status,
        isWarmup: set.isWarmup,
      })),
  );
  return foldBests(candidates, DEMO_PRIOR_BESTS);
}

function mutateRow(rows: GridRow[], rowId: string, fn: (row: GridRow) => GridRow): GridRow[] {
  return rows.map((row) => (row.id === rowId ? fn({ ...row, sets: row.sets.map((s) => ({ ...s })) }) : row));
}

export function isCommittedSet(set: GridSet): boolean {
  return !set.isWarmup && (set.status === 'done' || set.status === 'pr' || set.status === 'below');
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'hydrate':
      return { ...state, rows: action.rows, unit: action.unit, lastPr: null, past: [], hydrated: true };

    case 'clearPr':
      return { ...state, lastPr: null };

    case 'unit':
      return { ...state, unit: action.unit };

    case 'post':
      return { ...state, postSession: { ...state.postSession, ...action.patch } };

    case 'undo': {
      if (state.past.length === 0) return state;
      const previous = state.past[state.past.length - 1];
      if (!previous) return state;
      return { ...state, rows: previous, past: state.past.slice(0, -1) };
    }

    case 'reset':
      return { ...initialState(DEMO_SESSION), hydrated: true };

    case 'field': {
      const rows = mutateRow(state.rows, action.rowId, (row) => ({
        ...row,
        sets: row.sets.map((set) => (set.id === action.setId ? { ...set, ...action.patch } : set)),
      }));
      return { ...state, rows };
    }

    case 'note': {
      const rows = state.rows.map((row) => (row.id === action.rowId ? { ...row, notes: action.note } : row));
      return { ...state, rows };
    }

    case 'status': {
      const rows = mutateRow(state.rows, action.rowId, (row) => ({
        ...row,
        sets: row.sets.map((set) =>
          set.id === action.setId
            ? {
                ...set,
                status: action.status,
                isPr: action.status === 'pr',
                committedAt: action.status === 'pending' ? null : new Date().toISOString(),
              }
            : set,
        ),
      }));
      return withHistory(state, rows);
    }

    case 'add': {
      const count = action.count ?? 1;
      const rows = mutateRow(state.rows, action.rowId, (row) => {
        const last = [...row.sets].reverse().find((set) => set.status !== 'pending') ?? row.sets.at(-1);
        const weightKg = last?.weightKg ?? row.prescribed.targetWeightKg ?? 0;
        const reps = last?.reps ?? row.prescribed.repMax;
        const added: GridSet[] = Array.from({ length: count }, () => ({
          id: uid('set'),
          weightKg,
          reps,
          rpe: null,
          status: 'pending',
          isPr: false,
          isWarmup: false,
          source: 'web',
          committedAt: null,
          measure: row.measure,
          distanceM: row.prescribed.targetDistanceM,
          durationS: null,
          perSide: row.perSide,
          painScore: null,
        }));
        return { ...row, sets: [...row.sets, ...added] };
      });
      return withHistory(state, rows);
    }

    case 'delete': {
      const rows = mutateRow(state.rows, action.rowId, (row) => ({
        ...row,
        sets: row.sets.filter((set) => set.id !== action.setId),
      }));
      return withHistory(state, rows);
    }

    case 'commit': {
      const row = state.rows.find((r) => r.id === action.rowId);
      const target = row?.sets.find((s) => s.id === action.setId);
      if (!row || !target) return state;

      const merged: GridSet = { ...target, ...action.patch };
      const verdict = detectPr(
        { exerciseId: row.exerciseId, weightKg: merged.weightKg, reps: merged.reps },
        bestsExcluding(state.rows, action.setId),
      );

      const belowTarget = merged.reps < row.prescribed.repMin;
      const status: SetStatus = verdict.isPr ? 'pr' : belowTarget ? 'below' : 'done';

      const committed: GridSet = {
        ...merged,
        status,
        isPr: verdict.isPr,
        source: action.source ?? 'web',
        committedAt: new Date().toISOString(),
      };

      const rows = mutateRow(state.rows, action.rowId, (r) => ({
        ...r,
        sets: r.sets.map((set) => (set.id === action.setId ? committed : set)),
      }));

      const lastPr: PrEvent | null = verdict.isPr
        ? {
            exerciseName: row.name,
            weightKg: merged.weightKg,
            reps: merged.reps,
            e1rm: verdict.e1rm,
            previousBest: verdict.previousBest,
            deltaKg: verdict.deltaKg,
          }
        : null;

      const next = withHistory(state, rows);
      return lastPr ? { ...next, lastPr } : next;
    }

    default:
      return state;
  }
}

export interface WorkoutLogApi {
  rows: GridRow[];
  unit: UnitSystem;
  totals: SessionTotals;
  compliancePct: number;
  lastPr: PrEvent | null;
  canUndo: boolean;
  hasCommitted: boolean;
  setFields: (rowId: string, setId: string, patch: FieldPatch) => void;
  commitSet: (rowId: string, setId: string, patch?: FieldPatch, source?: SetSource) => void;
  setStatus: (rowId: string, setId: string, status: SetStatus) => void;
  setNote: (rowId: string, note: string) => void;
  addSet: (rowId: string, count?: number) => void;
  deleteSet: (rowId: string, setId: string) => void;
  toggleUnit: () => void;
  postSession: PostSessionScore;
  setPostSession: (patch: Partial<PostSessionScore>) => void;
  undo: () => void;
  reset: () => void;
  clearPr: () => void;
}

/**
 * The Grid's state machine: commit sets with server-identical PR detection,
 * add/delete, undo, unit switching, and localStorage persistence so a visitor's
 * demo session survives a refresh.
 */
export function useWorkoutLog(session: DemoSession = DEMO_SESSION): WorkoutLogApi {
  const [state, dispatch] = useReducer(reducer, session, initialState);

  useEffect(() => {
    const fallback = buildRows(session);
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { version?: number; rows?: GridRow[]; unit?: UnitSystem };
        if (parsed.version === 2 && Array.isArray(parsed.rows) && parsed.rows.length > 0) {
          dispatch({ type: 'hydrate', rows: parsed.rows, unit: parsed.unit === 'imperial' ? 'imperial' : 'metric' });
          return;
        }
      }
      dispatch({ type: 'hydrate', rows: fallback, unit: readUnitPreference() });
    } catch {
      dispatch({ type: 'hydrate', rows: fallback, unit: readUnitPreference() });
    }
    // deliberately run once on mount: this is hydration, not a data dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!state.hydrated) return;
    // keep every surface (The Grid, the Programme, the coach report) on one unit
    writeUnitPreference(state.unit);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 2, rows: state.rows, unit: state.unit }));
    } catch {
      /* storage blocked or full — the in-memory session still works */
    }
  }, [state.rows, state.unit, state.hydrated]);

  const totals = useMemo<SessionTotals>(() => {
    let volumeKg = 0;
    let totalReps = 0;
    let setsCompleted = 0;
    let setsPrescribed = 0;
    let prCount = 0;
    let failedSets = 0;
    let rpeSum = 0;
    let rpeCount = 0;

    for (const row of state.rows) {
      setsPrescribed += row.prescribed.sets;
      for (const set of row.sets) {
        if (set.isWarmup) continue;
        if (isCommittedSet(set)) {
          volumeKg += set.weightKg * set.reps;
          totalReps += set.reps;
          setsCompleted += 1;
          if (set.rpe !== null) {
            rpeSum += set.rpe;
            rpeCount += 1;
          }
        }
        if (set.status === 'pr') prCount += 1;
        if (set.status === 'failed') failedSets += 1;
      }
    }

    return {
      volumeKg: Math.round(volumeKg * 100) / 100,
      totalReps,
      setsCompleted,
      setsPrescribed,
      prCount,
      failedSets,
      avgRpe: rpeCount === 0 ? null : Math.round((rpeSum / rpeCount) * 10) / 10,
      durationMin: 0,
    };
  }, [state.rows]);

  const compliancePct =
    totals.setsPrescribed === 0 ? 0 : Math.round((totals.setsCompleted / totals.setsPrescribed) * 1000) / 10;

  const setFields = useCallback((rowId: string, setId: string, patch: FieldPatch) => {
    dispatch({ type: 'field', rowId, setId, patch });
  }, []);

  const commitSet = useCallback(
    (rowId: string, setId: string, patch?: FieldPatch, source?: SetSource) => {
      dispatch({ type: 'commit', rowId, setId, patch, source });
    },
    [],
  );

  const setStatus = useCallback((rowId: string, setId: string, status: SetStatus) => {
    dispatch({ type: 'status', rowId, setId, status });
  }, []);

  const setNote = useCallback((rowId: string, note: string) => {
    dispatch({ type: 'note', rowId, note });
  }, []);

  const addSet = useCallback((rowId: string, count?: number) => {
    dispatch({ type: 'add', rowId, count });
  }, []);

  const deleteSet = useCallback((rowId: string, setId: string) => {
    dispatch({ type: 'delete', rowId, setId });
  }, []);

  const toggleUnit = useCallback(() => {
    dispatch({ type: 'unit', unit: state.unit === 'metric' ? 'imperial' : 'metric' });
  }, [state.unit]);

  const setPostSession = useCallback((patch: Partial<PostSessionScore>) => {
    dispatch({ type: 'post', patch });
  }, []);

  const undo = useCallback(() => dispatch({ type: 'undo' }), []);
  const reset = useCallback(() => dispatch({ type: 'reset' }), []);
  const clearPr = useCallback(() => dispatch({ type: 'clearPr' }), []);

  return {
    rows: state.rows,
    unit: state.unit,
    totals,
    compliancePct,
    lastPr: state.lastPr,
    canUndo: state.past.length > 0,
    hasCommitted: totals.setsCompleted > 0,
    setFields,
    commitSet,
    setStatus,
    setNote,
    addSet,
    deleteSet,
    toggleUnit,
    postSession: state.postSession,
    setPostSession,
    undo,
    reset,
    clearPr,
  };
}
